<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');
const teamMembers = useMapGetter('teamMembers/getTeamMembers');
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const commercialTeamId = ref('');
const postSalesTeamId = ref('');
const managerId = ref('');
const keepSupervisorAccess = ref(false);
const isSaving = ref(false);
const eligibleManagers = computed(() => {
  const members = teamMembers.value(Number(commercialTeamId.value)) || [];
  const memberIds = new Set(members.map(member => member.id));
  return agents.value.filter(agent => memberIds.has(agent.id));
});

watch(
  currentAccount,
  account => {
    const settings = account?.settings || {};
    commercialTeamId.value =
      settings.conversation_handoff_commercial_team_id || '';
    postSalesTeamId.value =
      settings.conversation_handoff_post_sales_team_id || '';
    managerId.value = settings.conversation_handoff_manager_id || '';
    keepSupervisorAccess.value =
      Number(settings.conversation_handoff_supervisor_id) ===
      Number(currentUser.value?.id);
  },
  { immediate: true }
);

onMounted(() =>
  Promise.all([store.dispatch('teams/get'), store.dispatch('agents/get')])
);

watch(
  commercialTeamId,
  async teamId => {
    if (!teamId) return;

    try {
      await store.dispatch('teamMembers/get', { teamId });
    } catch {
      useAlert(t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.ERROR'));
    }
  },
  { immediate: true }
);

const save = async () => {
  if (
    commercialTeamId.value &&
    String(commercialTeamId.value) === String(postSalesTeamId.value)
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SAME_TEAM_ERROR'));
    return;
  }
  if (
    managerId.value &&
    !eligibleManagers.value.some(
      agent => String(agent.id) === String(managerId.value)
    )
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.INVALID_MANAGER'));
    return;
  }

  isSaving.value = true;
  try {
    await updateAccount({
      conversation_handoff_commercial_team_id:
        Number(commercialTeamId.value) || null,
      conversation_handoff_post_sales_team_id:
        Number(postSalesTeamId.value) || null,
      conversation_handoff_manager_id: Number(managerId.value) || null,
      ...(isSuperAdmin.value && {
        conversation_handoff_supervisor_id: keepSupervisorAccess.value
          ? currentUser.value.id
          : null,
      }),
    });
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.NOTE')"
    with-border
  >
    <form class="grid gap-4" @submit.prevent="save">
      <label class="grid gap-1 text-sm">
        <span>{{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.COMMERCIAL') }}</span>
        <select v-model="commercialTeamId" class="!mb-0">
          <option value="">
            {{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SELECT_TEAM') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </label>
      <label class="grid gap-1 text-sm">
        <span>{{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.POST_SALES') }}</span>
        <select v-model="postSalesTeamId" class="!mb-0">
          <option value="">
            {{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SELECT_TEAM') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </label>
      <label class="grid gap-1 text-sm">
        <span>{{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.MANAGER') }}</span>
        <select v-model="managerId" class="!mb-0">
          <option value="">
            {{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SELECT_MANAGER') }}
          </option>
          <option
            v-for="agent in eligibleManagers"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>
      </label>
      <label v-if="isSuperAdmin" class="flex items-center gap-2 text-sm">
        <input v-model="keepSupervisorAccess" type="checkbox" />
        <span>{{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SUPERVISOR') }}</span>
      </label>
      <div>
        <NextButton blue type="submit" :is-loading="isSaving">
          {{ t('GENERAL_SETTINGS.FORM.TEAM_HANDOFF.SAVE') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
